<!--
  PreviewFallbackToolsCard -- the SA switch for the Zone diagram and Open
  canvas previews on the product editor (Jake request 2026-10-28).

  Platform setting `preview_fallback_tools_enabled`, DEFAULT OFF: an unset
  row, a failed read and a malformed answer all present as OFF. Uploading
  your own image for a color is NOT governed by this switch (always
  available). Kept as its own component so the Platform Settings page only
  mounts it; the read and the write live here.
-->
<template>
    <div class="pft-card" data-testid="sa-setting-preview-fallback-tools">
        <div class="pft-card__body">
            <div class="pft-card__copy">
                <label class="pft-card__label" for="sa-setting-preview-fallback-tools-switch">
                    Zone diagram and open canvas previews
                </label>
                <p class="pft-card__help">
                    The product editor can offer a zone diagram and an open
                    canvas as extra ways to show a color's preview. Both are
                    off until you turn this on. Uploading your own image for
                    a color is always available and is not affected.
                </p>
            </div>

            <label class="pft-toggle" data-testid="sa-setting-preview-fallback-tools-toggle">
                <input
                    id="sa-setting-preview-fallback-tools-switch"
                    v-model="enabled"
                    type="checkbox"
                    class="pft-toggle__input"
                    data-testid="sa-setting-preview-fallback-tools-switch"
                    :disabled="saving"
                    @change="onToggle"
                />
                <span class="pft-toggle__track" aria-hidden="true">
                    <span class="pft-toggle__thumb"></span>
                </span>
                <span class="pft-toggle__state" data-testid="sa-setting-preview-fallback-tools-state">{{ enabled ? "On" : "Off" }}</span>
            </label>
        </div>

        <p
            v-if="errorMsg"
            class="pft-card__error"
            data-testid="sa-setting-preview-fallback-tools-error"
        >
            {{ errorMsg }}
        </p>
    </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import axios from "@/axios";

const URL = "/api/platform-settings/preview-fallback-tools-enabled";

// Default OFF, also when the read fails (never present the tools as on
// without an answer from the server).
const enabled = ref(false);
const saving = ref(false);
const errorMsg = ref("");

async function load() {
    errorMsg.value = "";
    try {
        const resp = await axios.get(URL);
        enabled.value =
            typeof resp?.data?.value === "boolean" ? resp.data.value : false;
    } catch (e) {
        enabled.value = false;
        errorMsg.value = "Could not load the zone diagram and open canvas setting.";
    }
}

async function onToggle() {
    const desired = enabled.value;
    saving.value = true;
    errorMsg.value = "";
    try {
        const resp = await axios.patch(URL, { value: desired });
        enabled.value = resp?.data?.value === true;
    } catch (e) {
        enabled.value = !desired;
        errorMsg.value =
            e?.response?.status === 403
                ? "Permission denied. Only Super Admins can change this setting."
                : "Could not save this setting.";
    } finally {
        saving.value = false;
    }
}

onMounted(load);
</script>

<style scoped>
.pft-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.pft-card__body {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
}
.pft-card__copy {
    min-width: 0;
}
.pft-card__label {
    display: block;
    font-weight: 600;
    color: #1f2937;
    font-size: 15px;
    margin-bottom: 4px;
    cursor: pointer;
}
.pft-card__help {
    margin: 0;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    max-width: 460px;
}
.pft-card__error {
    margin: 12px 0 0;
    color: #b91c1c;
    font-size: 13px;
}
.pft-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex: 0 0 auto;
}
.pft-toggle__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}
.pft-toggle__track {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    border-radius: 999px;
    background: #cbd2dc;
    transition: background 0.15s ease;
    flex: 0 0 auto;
}
.pft-toggle__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(28, 36, 48, 0.25);
    transition: transform 0.15s ease;
}
.pft-toggle__input:checked + .pft-toggle__track {
    background: #2f6df6;
}
.pft-toggle__input:checked + .pft-toggle__track .pft-toggle__thumb {
    transform: translateX(18px);
}
.pft-toggle__input:focus-visible + .pft-toggle__track {
    outline: 2px solid #2f6df6;
    outline-offset: 2px;
}
.pft-toggle__state {
    font-size: 13px;
    color: #5b6572;
}
</style>
